import { IAccurateEstimatedOfferResponse } from '@services/getAccurateEstimatedOffer';
import { IPostPlateToVINResponse } from '@services/postPlateToVIN';
import { ICurrentAddressInfo } from '@components/TwoYearsAddressHistoryForm/TwoYearsAddressHistoryForm';
import { IContactDetailsFromMetadataResponse } from '@services/contactDetails';
import { ICreateContactResponse } from '../services/verifyYourInformation';
import { Address } from './proveIdentity';

export interface IBranding {
  hideTopNav: boolean;
  primaryColor: string;
  secondaryColor: string;
  roundCorners: boolean;
}

export interface IWidgetInitVariables {
  PartnerName: string;
  branding: IBranding;
  tracking: {
    extraData: string;
  };
}

export interface ILicensePlateInfoForm {
  plateState: string;
  plateNumber: string;
  estimatedMileageRange: string;
  currentMonthlyPayment: number;

}
export interface IVerifyInfoForm {
  infoFormData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
}
export interface IManualLicensePlateInfoForm {
  year: string;
  make: string;
  makeName?: string;
  model: string;
  estimatedMileage: string;
  currentMonthlyPayment: number;
}

export interface IContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface IVerifyYourInformation {
  firstName: string;
  lastName: string;
  addresses: Address[];
  phone: string;
  ssn: string;
  dateOfBirth: string;
  email: string;
  residency: string;
}

export interface IVerifyYourInfoManuallyAddress {
  streetAddress: string;
  extendedAddress: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface IEmploymentForm {
  incomeType: string;
  employersName: string;
  jobTitle: string;
  employersAddress: string;
  employersAPTSuiteNumber: string;
  employersZipCode: string;
  employersCity: string;
  employersState: string;
  employersPhoneNumber: string;
  startDate: string;
  isCurrentIncomeSource: boolean;
  annualIncome: number;
  endDate: string;
}

export interface INonEmploymentForm {
  incomeType: string;
}

export interface IIncomeForm {
  incomeType: string;
  otherIncomeSource: string;
  startDate: string;
  isCurrentIncomeSource: boolean;
  endDate: string;
  monthlyAmount: number;
}

export interface IVerifyYourIdentity {
  dateOfBirth: string;
  phone: string;
}

export interface ErrorResponse {
  error: {
    status?: number | string;
    message: string;
  };
}

export interface AddressInfo {
  isCurrent: boolean;
  includeInDti: boolean;
  streetLine1: string;
  streetLine2: string | null;
  city: string;
  state: string;
  zip: string;
  county: string | null;
  unit: string;
  monthsAtAddress: number;
  type: string;
  monthlyPayment: number;
}

export interface UpdateAddressInfo {
  isCurrent: boolean;
  monthsAtAddress: number;
  streetLine1: string;
  streetLine2: string;
  zip: string;
  city: string;
  state: string;
  type: string;
  county: string | null;
  monthlyPayment: number;
}

export interface FormState {
  widgetInitVariables: IWidgetInitVariables;
  licensePlateInfoForm: ILicensePlateInfoForm;
  manualLicensePlateInfoForm: IManualLicensePlateInfoForm;
  verifyYourIdentity: IVerifyYourIdentity;
  licensePlateInfoResponse: IPostPlateToVINResponse | null;
  verifyYourInformation: IVerifyYourInformation;
  createContactResponse: ICreateContactResponse | null;
  softPullResponse: IAccurateEstimatedOfferResponse[] | null;
  offerSelectionFormData: IAccurateEstimatedOfferResponse | null;
  addressHistory: ICurrentAddressInfo[] | null;
  employmentFormData: IEmploymentForm | INonEmploymentForm;
  incomeFormData: IIncomeForm;
  addressContactResponse: AddressInfo[] | null;
  currentStep: string;
  progress: number;
  isLoading: boolean;
  isSubmitSuccess: boolean;
  error: string | number | null;
  apiError: ErrorResponse | null;
  contactDetails: IContactDetailsFromMetadataResponse | null;
}

export interface FormErrors {
  [key: string]: string;
}

export const CDX_ACTIONS = {
  SET_INIT_VALUES: 'SET_INIT_VALUES',
  SET_STEP: 'SET_STEP',
  RESET_FORM: 'RESET_FORM',
  SUBMIT_START: 'SUBMIT_START',
  SUBMIT_SUCCESS: 'SUBMIT_SUCCESS',
  SUBMIT_ERROR: 'SUBMIT_ERROR',
  SUBMIT_API_ERROR: 'SUBMIT_API_ERROR',
  GET_API_ERROR: 'GET_API_ERROR',
  SET_LICENSE_PLATE_INFO_FORM: 'SET_LICENSE_PLATE_INFO_FORM',
  SET_MANUAL_LICENSE_PLATE_INFO_FORM: 'SET_MANUAL_LICENSE_PLATE_INFO_FORM',
  SET_VERIFY_YOUR_IDENTITY_FORM: 'SET_VERIFY_YOUR_IDENTITY_FORM',
  SET_VERIFY_YOUR_INFORMATION_FORM: 'SET_VERIFY_YOUR_INFORMATION_FORM',
  SET_EMPLOYMENT_FORM: 'SET_EMPLOYMENT_FORM',
  SET_INCOME_FORM: 'SET_INCOME_FORM',
  SUBMIT_LICENSE_PLATE_INFO_SUCCESS: 'SUBMIT_LICENSE_PLATE_INFO_SUCCESS',
  SUBMIT_CREATE_CONTACT_SUCCESS: 'SUBMIT_CREATE_CONTACT_SUCCESS',
  SET_ADDRESS_HISTORY: 'SET_ADDRESS_HISTORY',
  SET_ADDRESS_CONTACT_RESPONSE: 'SET_ADDRESS_CONTACT_RESPONSE',
  SUBMIT_SOFTPULL_SUCCESS: 'SUBMIT_SOFTPULL_SUCCESS',
  SET_OFFER_SELECTION: 'SET_OFFER_SELECTION',
  SUBMIT_LICENSE_PLATE_INFO_START: 'SUBMIT_LICENSE_PLATE_INFO_START',
  GET_CONTACT_DETAILS_SUCCESS: 'GET_CONTACT_DETAILS_SUCCESS',
  SET_LOADING: 'SET_LOADING',
} as const;

export type FormActionType =
  | { type: 'SET_INIT_VALUES'; payload: Partial<IWidgetInitVariables> }
  | { type: 'SET_STEP'; payload: string }
  | { type: 'RESET_FORM' }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_SUCCESS' }
  | { type: 'SUBMIT_ERROR'; payload: string | number | null }
  | { type: 'SUBMIT_API_ERROR'; payload: ErrorResponse | null }
  | { type: 'GET_API_ERROR'; payload: ErrorResponse | null }
  | { type: 'SET_LICENSE_PLATE_INFO_FORM'; payload: Partial<ILicensePlateInfoForm> }
  | { type: 'SET_MANUAL_LICENSE_PLATE_INFO_FORM'; payload: Partial<IManualLicensePlateInfoForm> }
  | { type: 'SET_VERIFY_YOUR_IDENTITY_FORM'; payload: Partial<IVerifyYourIdentity> }
  | { type: 'SET_VERIFY_YOUR_INFORMATION_FORM'; payload: Partial<IVerifyYourInformation> }
  | { type: 'SET_EMPLOYMENT_FORM'; payload: Partial<IEmploymentForm> }
  | { type: 'SET_INCOME_FORM'; payload: Partial<IIncomeForm> }
  | { type: 'SUBMIT_LICENSE_PLATE_INFO_SUCCESS'; payload: IPostPlateToVINResponse | null }
  | { type: 'SUBMIT_CREATE_CONTACT_SUCCESS'; payload: ICreateContactResponse | null }
  | { type: 'SET_ADDRESS_HISTORY'; payload: ICurrentAddressInfo[] | null }
  | { type: 'SET_ADDRESS_CONTACT_RESPONSE'; payload: AddressInfo[] | null }
  | { type: 'SUBMIT_SOFTPULL_SUCCESS'; payload: IAccurateEstimatedOfferResponse[] | null }
  | { type: 'SET_OFFER_SELECTION'; payload: IAccurateEstimatedOfferResponse }
  | { type: 'SUBMIT_LICENSE_PLATE_INFO_START' }
  | { type: 'GET_CONTACT_DETAILS_SUCCESS'; payload: IContactDetailsFromMetadataResponse | null}
  | { type: 'SET_LOADING'; payload: boolean}

export interface IVerifyMobileNumberForm {
  verificationCode: string;
}
